function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { DropdownMenu, TableList, TableListActions, Typography, useOverlayContext } from 'seagull';
import { CopyFill, Edit1Liner24, TrashLiner24 } from 'seagull/icons';
import { routes } from "../../../constants";
import { useDuplicateRelationship } from "../../../hooks/useDuplicateRelationship";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useScheduledProposal } from "../../../hooks/useScheduledProposal";
import { useTerminateAgreement } from "../../../hooks/useTerminateAgreement";
import { formatDateWithTime } from "../../../models/time/selectors";
import AdditionalRecipientsCell from "./draftCells/AdditionalRecipientsCell";
import AgreementNameCell from "./draftCells/AgreementNameCell";
import AgreementNoteCell from "./draftCells/AgreementNoteCell";
import AutoApproveAmendmentsCell from "./draftCells/AutoApproveAmendments";
import AutoApproveOutOfScopeChargesCell from "./draftCells/AutoApproveOutOfScopeChargesCell";
import ContactCell from "./draftCells/ContactCell";
import CreditCardProcessingFeesCell from "./draftCells/CreditCardProcessingFeesCell";
import EffectiveDateCell from "./draftCells/EffectiveDateCell";
import ExpirationDateCell from "./draftCells/ExpirationDateCell";
import IntroductoryMessageCell from "./draftCells/IntroductoryMessageCell";
import IntroductoryVideoCell from "./draftCells/IntroductoryVideoCell";
import ManualPaymentApprovalCell from "./draftCells/ManualPaymentApprovalCell";
import NetTermsCell from "./draftCells/NetTermsCell";
import PaymentMethodRequiredCell from "./draftCells/PaymentMethodRequiredCell";
import TermsOfServiceCell from "./draftCells/TermsOfServiceCell";
import DraftUpdateModal from "./DraftUpdateModal";
import EditDraftServiceTable from "./EditDraftServiceTable/EditDraftServiceTable";
import { getModifiedBy, makeEditableTableItem } from "./EditDraftsTable.utils";
import styles from "./EditDraftsTableRow.module.scss";
import { useUpdateDraft } from "./hooks/useUpdateDraft";
import { EditDraftsTableColumns } from "./types";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testIdPrefix = 'edit-drafts-table';
const EditDraftsTableRow = props => {
  const {
    index,
    visibleColumns,
    draft,
    getContact,
    updateDraft,
    deleteDraft
  } = props;
  const history = useHistory();
  const {
    duplicateDraftFromId
  } = useDuplicateRelationship();
  const {
    terminateDraftById
  } = useTerminateAgreement();
  const {
    ADD_SERVICES_AUTOCOMPLETE
  } = useFeatureFlags();
  const contact = getContact(draft);
  const {
    showModal,
    startLoader,
    stopLoader
  } = useOverlayContext();
  const {
    updateDraft: saveDraft
  } = useUpdateDraft();
  const hasNoServicesOrBundles = draft.hasNoServicesOrBundles();
  const isDisabledForScheduledDrafts = draft.isScheduled();
  const {
    showSwitchToEditModal
  } = useScheduledProposal({
    draftId: draft.id(),
    refresh: () => {},
    isSampleRelationship: draft.isSample()
  });
  const editDraft = () => {
    if (isDisabledForScheduledDrafts) {
      showSwitchToEditModal();
    } else {
      history.push(routes.draft(draft.id()));
    }
  };
  const openDraftUpdateModal = _ref => {
    let {
      width,
      renderContent,
      shouldSubmit,
      skin,
      submitOnNotDirty
    } = _ref;
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(DraftUpdateModal, {
          skin: skin,
          draft: draft,
          width: width,
          renderContent: renderContent,
          dismissModal: dismissModal,
          changeDraftInRow: updateDraft,
          shouldSubmit: shouldSubmit,
          submitOnNotDirty: submitOnNotDirty
        });
      }
    });
  };
  const EditableCellDefaultProps = {
    draft,
    index,
    onOpenModal: openDraftUpdateModal,
    isDisabled: isDisabledForScheduledDrafts,
    disabledTooltip: isDisabledForScheduledDrafts ? 'In order to edit a scheduled proposal, its scheduling must be canceled.' : undefined
  };
  function onAddServices(services, packageId) {
    const newDraft = draft.addServices(services, packageId);
    const loaderName = 'addServicesLoader';
    saveDraft({
      newDraft,
      onSuccess: () => updateDraft(newDraft),
      onStartLoader: () => startLoader(loaderName),
      onStopLoader: () => stopLoader(loaderName)
    });
  }
  return /*#__PURE__*/_jsx(TableList.Row, {
    rowId: draft.id(),
    "data-testid": "".concat(testIdPrefix, "-row-").concat(draft.id()),
    items: compact([makeEditableTableItem( /*#__PURE__*/_jsx(ContactCell, _objectSpread(_objectSpread({}, EditableCellDefaultProps), {}, {
      getContact: getContact
    }))), visibleColumns[EditDraftsTableColumns.AdditionalRecipients] && makeEditableTableItem( /*#__PURE__*/_jsx(AdditionalRecipientsCell, _objectSpread(_objectSpread({}, EditableCellDefaultProps), {}, {
      contact: contact
    }))), visibleColumns[EditDraftsTableColumns.AgreementName] && makeEditableTableItem( /*#__PURE__*/_jsx(AgreementNameCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.EffectiveDate] && makeEditableTableItem( /*#__PURE__*/_jsx(EffectiveDateCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.ModifiedBy] && {
      value: /*#__PURE__*/_jsx(Typography, {
        "data-testid": "last-modified-by-".concat(index),
        children: getModifiedBy(draft)
      })
    }, visibleColumns[EditDraftsTableColumns.LastModified] && {
      value: /*#__PURE__*/_jsx(Typography, {
        "data-testid": "last-modified-on-".concat(index),
        children: formatDateWithTime(draft.lastModifiedOn())
      })
    }, visibleColumns[EditDraftsTableColumns.ExpirationDate] && makeEditableTableItem( /*#__PURE__*/_jsx(ExpirationDateCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.IntroductoryMessage] && makeEditableTableItem( /*#__PURE__*/_jsx(IntroductoryMessageCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.IntroductoryVideo] && makeEditableTableItem( /*#__PURE__*/_jsx(IntroductoryVideoCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.TermsOfService] && makeEditableTableItem( /*#__PURE__*/_jsx(TermsOfServiceCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.AgreementNote] && makeEditableTableItem( /*#__PURE__*/_jsx(AgreementNoteCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.AutoApproveAmendments] && makeEditableTableItem( /*#__PURE__*/_jsx(AutoApproveAmendmentsCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.AutoApproveOutOfScopeCharges] && makeEditableTableItem( /*#__PURE__*/_jsx(AutoApproveOutOfScopeChargesCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue] && makeEditableTableItem( /*#__PURE__*/_jsx(NetTermsCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.PaymentMethodRequiredForSigning] && makeEditableTableItem( /*#__PURE__*/_jsx(PaymentMethodRequiredCell, _objectSpread({}, EditableCellDefaultProps))), visibleColumns[EditDraftsTableColumns.CreditCardProcessingFees] && makeEditableTableItem( /*#__PURE__*/_jsx(CreditCardProcessingFeesCell, _objectSpread(_objectSpread({}, EditableCellDefaultProps), {}, {
      contact: contact
    }))), visibleColumns[EditDraftsTableColumns.ManualApprovalOfEachPayment] && makeEditableTableItem( /*#__PURE__*/_jsx(ManualPaymentApprovalCell, _objectSpread({}, EditableCellDefaultProps)))]),
    toggleExpandProps: ADD_SERVICES_AUTOCOMPLETE ? undefined : {
      disabled: hasNoServicesOrBundles,
      tooltipText: hasNoServicesOrBundles ? /*#__PURE__*/_jsx("div", {
        "data-testid": "no-services-tooltip",
        children: "No services have been added to this proposal draft yet"
      }) : undefined
    },
    actionColumn: /*#__PURE__*/_jsxs(TableListActions, {
      testId: "table-list-actions-".concat(draft.id()),
      children: [/*#__PURE__*/_jsx(DropdownMenu.Item, {
        iconStart: /*#__PURE__*/_jsx(Edit1Liner24, {}),
        onClick: editDraft,
        "data-testid": "table-list-actions-edit-".concat(draft.id()),
        children: "Edit Draft"
      }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
        iconStart: /*#__PURE__*/_jsx(CopyFill, {}),
        onClick: () => duplicateDraftFromId(draft.id()),
        "data-testid": "table-list-actions-duplicate-".concat(draft.id()),
        children: "Duplicate Draft"
      }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
        skin: "error",
        iconStart: /*#__PURE__*/_jsx(TrashLiner24, {}),
        onClick: () => terminateDraftById(draft.id(), () => deleteDraft(draft)),
        "data-testid": "table-list-actions-discard-".concat(draft.id()),
        children: "Discard Draft"
      })]
    }),
    expandedContent: () => /*#__PURE__*/_jsx("div", {
      className: styles.expandedContent,
      "data-testid": "expanded-content-".concat(draft.id()),
      children: /*#__PURE__*/_jsx(EditDraftServiceTable, _objectSpread(_objectSpread({}, EditableCellDefaultProps), {}, {
        onAddServices: onAddServices
      }))
    })
  });
};
export default EditDraftsTableRow;